import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '../common/Table';
import Button from '../common/Button';
import Input from '../common/Input';
import Pagination from '../common/Pagination';
import Card from '../common/Card';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/customer-list.css';

const CustomerList = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadCustomers();
  }, [pagination.page]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm) {
        searchCustomers();
      } else {
        loadCustomers();
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/customers.php', {
        params: {
          page: pagination.page,
          limit: pagination.limit
        }
      });
      setCustomers(response.data.customers);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchCustomers = async () => {
    if (!searchTerm) return;
    
    setLoading(true);
    try {
      const response = await api.get('/customers.php/search', {
        params: { q: searchTerm }
      });
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Failed to search customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await api.delete(`/customers.php/${id}`);
      loadCustomers();
    } catch (error) {
      console.error('Failed to delete customer:', error);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Customer',
      render: (_, row) => (
        <div className="customer-cell">
          <div className="customer-avatar">
            {row.first_name?.[0]}{row.last_name?.[0]}
          </div>
          <div>
            <div className="customer-name">{row.first_name} {row.last_name}</div>
            <div className="customer-phone">{row.phone}</div>
          </div>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      render: (value) => value || '-'
    },
    {
      key: 'total_purchases',
      label: 'Total Purchases',
      render: (value) => formatCurrency(value || 0)
    },
    {
      key: 'current_balance',
      label: 'Balance',
      render: (value) => (
        <span className={value > 0 ? 'balance-positive' : 'balance-zero'}>
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'last_purchase',
      label: 'Last Purchase',
      render: (value) => value ? formatDate(value) : 'Never'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button 
            className="icon-btn view"
            onClick={() => navigate(`/customers/${row.id}`)}
            title="View Details"
          >
            👁️
          </button>
          <button 
            className="icon-btn edit"
            onClick={() => navigate(`/customers/${row.id}/edit`)}
            title="Edit"
          >
            ✏️
          </button>
          {row.current_balance > 0 && (
            <button 
              className="icon-btn payment"
              onClick={() => navigate(`/debts/payment?customer=${row.id}`)}
              title="Record Payment"
            >
              💰
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="customer-list">
      <Card>
        <div className="customer-list-header">
          <h2>Customers</h2>
          <div className="header-actions">
            <Button 
              variant="secondary"
              onClick={() => navigate('/customers/bulk-upload')}
              icon="📤"
            >
              Bulk Upload
            </Button>
            <Button 
              variant="primary"
              onClick={() => navigate('/customers/new')}
              icon="➕"
            >
              Add Customer
            </Button>
          </div>
        </div>

        <div className="customer-list-filters">
          <Input
            type="text"
            placeholder="Search by name, phone or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon="🔍"
            fullWidth
          />
        </div>

        <Table
          columns={columns}
          data={customers}
          loading={loading}
          onRowClick={(row) => navigate(`/customers/${row.id}`)}
        />

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          onPageChange={(page) => setPagination({ ...pagination, page })}
        />
      </Card>
    </div>
  );
};

export default CustomerList;